import React, { cloneElement, useEffect, useMemo, useState } from "react";
import { PageHeader } from "../../components/layout/header/Header";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { useOrganizationVendors, usePrimaryOrganization } from "../../hooks";
import { useOnboardingProgress } from "../../../common/hooks/useOnboardingProgress";
import { DashboardCompletionStepBox } from "@truevault/aquilo";
import { Routes } from "../../root/routes";
import { useSurvey } from "../../../common/hooks/api/useSurvey";
import { useDataMap } from "../../../common/hooks/api/useDataMap";
import { usePrivacyCenterSettings } from "../../components/superorg/settings/PrivacyCenterSettings";
import { BUSINESS_SURVEY_NAME } from "../../surveys/businessSurvey";
import {
  CONSUMER_INCENTIVES_SURVEY_NAME,
  OFFERS_CONSUMER_INCENTIVES_SLUG,
} from "../../surveys/consumerCollectionIncentivesSurvey";
import { EEA_UK_SURVEY_NAME } from "../../surveys/eeaUkSurvey";
import { HR_SURVEY_NAME } from "../../surveys/hrSurvey";
import { computeBusinessProfileSurveyCompletion } from "../../surveys/businessProfileSurveyCompletion";
import { allSurveyQuestionsWithVisibility } from "../surveys/BusinessSurveyAquiloPage";
import { PageLoading } from "../../components";
import { useEditPrivacyCenter } from "../../components/privacyCenter/EditPrivacyCenter";
import { usePrivacyCenterNotice } from "../../hooks/usePrivacyCenterNotice";
import { useDataRetentionModule } from "../../components/organisms/data-retention/DataRetentionModule";
import { isDataRetentionComplete } from "./StayCompliantDataRetentionPage";
import {
  hasVendorEverBeenContacted,
  isVendorDocumentationClassified,
} from "./vendor-documentation/VendorDocumentationPage";
import { VerticalProgressBarStepStatus } from "@truevault/aquilo/src/components/progressBar/VerticalProgressBarCirclesWithText";
import { isVendorInventoryComplete } from "./vendor-inventory/VendorInventoryPage";
import { CONSUMER_SOURCES_SURVEY_NAME } from "../get-compliant/data-map/ConsumerDataSourcesPage";
import { useNonEmploymentCollectionGroups } from "../../hooks/useCollectionGroups";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../get-compliant/data-recipients/shared";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "../stay-compliant/PrivacyNoticeChecklistItems";

export const DevInstructionsFinishedDataMapOnboarding = "finished-data-map-onboarding";

type StepState = {
  complete: boolean;
  stepStatus: VerticalProgressBarStepStatus;
};

type DataMapDashboardProps = {
  isSurveyComplete: boolean;
  vendorInventory: StepState;
  dataFlows: StepState;
  vendorDocumentation: StepState;
  dataRetention: StepState;
};

function DataMapDashboard({
  isSurveyComplete,
  vendorInventory,
  dataFlows,
  vendorDocumentation,
  dataRetention,
}: DataMapDashboardProps) {
  const [org] = usePrimaryOrganization();
  const { privacyCenter } = usePrivacyCenterSettings();

  if (!org || !privacyCenter) {
    return <PageLoading />;
  }

  // Force unlock of all nav items after org has completed onboarding latch (POL-7563)
  const forceUnlocked = org.finishedOnboardingLatch;

  const vendorInventoryItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/privacy_center/icons/data-map-icon.svg"
      title="Vendor List"
      description="Add your vendors and data disclosures"
      status={vendorInventory.complete ? "complete" : "finish-set-up"}
    />
  );

  const dataFlowsItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/privacy_center/icons/vendor-documentation-icon.svg"
      title="Data Flows"
      description="Configure your data flows"
      status={dataFlows.complete ? "complete" : "finish-set-up"}
    />
  );

  const vendorDocumentationItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/privacy_center/icons/vendor-documentation-icon.svg"
      title="Vendor Documents"
      description="Review your vendors classification and documentation"
      status={vendorDocumentation.complete ? "complete" : "finish-set-up"}
    />
  );

  const dataRetentionItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/privacy_center/icons/data-retention-icon.svg"
      title="Data Retention"
      description="Configure your data retention policy"
      status={dataRetention.complete ? "complete" : "finish-set-up"}
    />
  );

  return (
    <StandardPageWrapper fullWidth>
      <PageHeader titleContent="Data Map" marginBottomPx={24} />

      <div className="tw-boundary">
        <div className="mb-[48px]">
          <span className="font-bold text-neutral-700">Business Account:</span>
          &nbsp;
          <span>
            {org.name} · {privacyCenter.requestEmail}
          </span>
        </div>

        <div className="grid grid-cols-[1fr_1fr] gap-[16px] max-w-[830px] mt-[48px]">
          <a href={Routes.organization.settings.businessSurvey}>
            <DashboardCompletionStepBox
              iconFilename="/assets/images/stay_compliant/privacy_center/icons/business-survey-icon.svg"
              title="Business Profile"
              description="Answer questions about your business"
              status={isSurveyComplete ? "complete" : "finish-set-up"}
            />
          </a>

          {forceUnlocked || vendorInventory.stepStatus !== "upcoming" ? (
            <a href={Routes.dataMap.vendorInventory}>{vendorInventoryItem}</a>
          ) : (
            cloneElement(vendorInventoryItem, { isDimmed: true })
          )}

          {forceUnlocked || dataFlows.stepStatus !== "upcoming" ? (
            <a href={Routes.dataMap.dataFlows.dashboard}>{dataFlowsItem}</a>
          ) : (
            cloneElement(dataFlowsItem, { isDimmed: true })
          )}

          {forceUnlocked || vendorDocumentation.stepStatus !== "upcoming" ? (
            <a href={Routes.dataMap.vendorDocumentation}>{vendorDocumentationItem}</a>
          ) : (
            cloneElement(vendorDocumentationItem, { isDimmed: true })
          )}

          {forceUnlocked || dataRetention.stepStatus !== "upcoming" ? (
            <a href={Routes.dataMap.dataRetention}>{dataRetentionItem}</a>
          ) : (
            cloneElement(dataRetentionItem, { isDimmed: true })
          )}
        </div>
      </div>
    </StandardPageWrapper>
  );
}

// Hooks that compute the Business Profile and Vendor List completion
// statuses. These don't depend on a consumer collection group existing.
export function useBusinessProfileAndVendorInventoryStatus(options?: {
  // When false, skip survey / privacy-center / dataMap fetches and treat survey
  // completion as done. Used by StayCompliantNav when finishedOnboardingLatch
  // already unlocks all steps.
  enabled?: boolean;
}) {
  const [org] = usePrimaryOrganization();
  const enabled = options?.enabled ?? true;
  const notReady = !enabled;

  const { settings: pcSettings, privacyCenter } = usePrivacyCenterSettings({ enabled });

  const {
    answers: bizAnswers,
    remote: bizRemote,
    flaggedForReviewSlugs: bizFlaggedForReviewSlugs,
  } = useSurvey(org?.id, BUSINESS_SURVEY_NAME, undefined, notReady);

  const {
    answers: incentivesAnswers,
    remote: incentivesRemote,
    flaggedForReviewSlugs: incentivesFlaggedForReviewSlugs,
  } = useSurvey(org?.id, CONSUMER_INCENTIVES_SURVEY_NAME, undefined, notReady);

  const { answers: sourcesAnswers, flaggedForReviewSlugs: sourcesFlaggedForReviewSlugs } =
    useSurvey(org?.id, CONSUMER_SOURCES_SURVEY_NAME, undefined, notReady);

  const {
    answers: eeaAnswers,
    remote: eeaRemote,
    flaggedForReviewSlugs: eeaFlaggedForReviewSlugs,
  } = useSurvey(org?.id, EEA_UK_SURVEY_NAME, undefined, notReady);

  const {
    answers: hrAnswers,
    remote: hrRemote,
    flaggedForReviewSlugs: hrFlaggedForReviewSlugs,
  } = useSurvey(org?.id, HR_SURVEY_NAME, undefined, notReady);

  const allFlaggedForReviewSlugs = useMemo(
    () => [
      ...bizFlaggedForReviewSlugs,
      ...incentivesFlaggedForReviewSlugs,
      ...sourcesFlaggedForReviewSlugs,
      ...hrFlaggedForReviewSlugs,
      ...eeaFlaggedForReviewSlugs,
    ],
    [
      bizFlaggedForReviewSlugs,
      incentivesFlaggedForReviewSlugs,
      sourcesFlaggedForReviewSlugs,
      hrFlaggedForReviewSlugs,
      eeaFlaggedForReviewSlugs,
    ],
  );
  const [allVendors] = useOrganizationVendors(
    enabled ? org?.id : undefined,
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ undefined,
    /* onlyUseSettled = */ true,
  );
  const { answers: sellingAndSharingAnswers, remote: sellingAndSharingRemote } = useSurvey(
    org?.id,
    SELLING_AND_SHARING_SURVEY_NAME,
    undefined,
    notReady,
  );
  const { data: dataMap, remote: remoteDataMap } = useDataMap(org?.id, notReady);

  const [isSurveyComplete, setIsSurveyComplete] = useState<boolean | null>(null);
  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (
      // Surveys and the data map hand back empty defaults while loading, so gate on the
      // fetched results; this value is latched, so computing it early would stick.
      isSurveyComplete === null &&
      org &&
      privacyCenter &&
      bizRemote &&
      incentivesRemote &&
      eeaRemote &&
      hrRemote &&
      allVendors &&
      sellingAndSharingRemote &&
      remoteDataMap &&
      pcSettings
    ) {
      let { bizQuestionsWithVisibility, hrQuestionsWithVisibility, eeaQuestionsWithVisibility } =
        allSurveyQuestionsWithVisibility(org, privacyCenter, pcSettings, {
          bizAnswers,
          sellingAndSharingAnswers,
          dataRecipients: allVendors,
          dataMap,
          hrAnswers,
          eeaAnswers,
          incentivesAnswers,
        });
      let bizSurveyVisibleQuestions = bizQuestionsWithVisibility.filter((q) => q.visible);
      let hrVisibleQuestions = hrQuestionsWithVisibility.filter((q) => q.visible);
      let eeaVisibleQuestions = eeaQuestionsWithVisibility.filter((q) => q.visible);
      const { isSurveyComplete: surveyComplete } = computeBusinessProfileSurveyCompletion({
        featureGdpr: org?.featureGdpr,
        privacyCenter,
        pcSettings,
        bizVisibleQuestions: bizSurveyVisibleQuestions,
        bizAnswers,
        hrVisibleQuestions,
        hrAnswers,
        eeaVisibleQuestions,
        eeaAnswers,
        financialIncentiveAnswer: incentivesAnswers[OFFERS_CONSUMER_INCENTIVES_SLUG],
      });
      setIsSurveyComplete(surveyComplete);
    }
  }, [
    enabled,
    org,
    privacyCenter,
    bizAnswers,
    incentivesAnswers,
    sourcesAnswers,
    eeaAnswers,
    hrAnswers,
    allVendors,
    sellingAndSharingAnswers,
    dataMap,
    bizRemote,
    incentivesRemote,
    eeaRemote,
    hrRemote,
    sellingAndSharingRemote,
    remoteDataMap,
    pcSettings,
    isSurveyComplete,
    setIsSurveyComplete,
    allFlaggedForReviewSlugs,
  ]);

  const [isVendorDocumentationFinished, setIsVendorDocumentationFinished] = useState<
    boolean | null
  >(null);
  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (isVendorDocumentationFinished === null && allVendors) {
      setIsVendorDocumentationFinished(
        allVendors.length > 0 &&
          allVendors.every(
            (v) => isVendorDocumentationClassified(org, v) || hasVendorEverBeenContacted(v),
          ),
      );
    }
  }, [enabled, allVendors, isVendorDocumentationFinished, setIsVendorDocumentationFinished, org]);

  const [isVendorInventoryFinished, setIsVendorInventoryFinished] = useState<boolean | null>(null);
  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (isVendorInventoryFinished === null && allVendors && sellingAndSharingRemote) {
      setIsVendorInventoryFinished(
        // Check `allVendors.length > 1` since we always add TrueVault by default
        allVendors.length > 1 &&
          allVendors.every((v) =>
            isVendorInventoryComplete(v, sellingAndSharingAnswers, "CONSUMER"),
          ),
      );
    }
  }, [
    enabled,
    allVendors,
    isVendorInventoryFinished,
    setIsVendorInventoryFinished,
    sellingAndSharingAnswers,
    sellingAndSharingRemote,
  ]);

  return {
    allVendors,
    // When disabled (onboarding latch), treat steps as complete — callers that disable
    // only need this for unlock gating, which the latch already satisfies.
    isSurveyComplete: enabled ? isSurveyComplete : true,
    isVendorInventoryFinished: enabled ? isVendorInventoryFinished : true,
    isVendorDocumentationFinished: enabled ? isVendorDocumentationFinished : true,
  };
}

function DataMapDashboardWithConsumerGroup() {
  const [org] = usePrimaryOrganization();

  const [onboardingProgress] = useOnboardingProgress(org?.id);

  const { allVendors, isSurveyComplete, isVendorInventoryFinished, isVendorDocumentationFinished } =
    useBusinessProfileAndVendorInventoryStatus();

  const editPrivacyCenter = useEditPrivacyCenter();

  const { privacyCenter } = usePrivacyCenterSettings();

  const [draftNotice] = usePrivacyCenterNotice(
    org?.id,
    privacyCenter?.id,
    editPrivacyCenter?.props?.draftPrivacyPolicyNoticeId,
  );

  const dataRetentionModule = useDataRetentionModule();

  const [isRetentionFinished, setIsRetentionFinished] = useState<boolean | null>(null);
  useEffect(() => {
    if (draftNotice && editPrivacyCenter && dataRetentionModule) {
      const isFinished = isDataRetentionComplete(
        draftNotice,
        editPrivacyCenter,
        dataRetentionModule,
      );
      setIsRetentionFinished(isFinished);
    }
  }, [
    draftNotice,
    editPrivacyCenter,
    dataRetentionModule,
    isRetentionFinished,
    setIsRetentionFinished,
  ]);

  const { answers: devInstructionsAnswers } = useSurvey(
    org?.id,
    DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  );
  const isDataMapOnboardingComplete =
    devInstructionsAnswers &&
    devInstructionsAnswers[DevInstructionsFinishedDataMapOnboarding] === "true";

  if (
    isSurveyComplete === null ||
    isRetentionFinished === null ||
    isVendorDocumentationFinished === null ||
    isVendorInventoryFinished === null ||
    !onboardingProgress
  ) {
    return <PageLoading />;
  }

  const isDataFlowsFinished = onboardingProgress.dataMapOnboardingProgress.isDataFlowsComplete;

  let vendorInventoryStepStatus: VerticalProgressBarStepStatus = isVendorInventoryFinished
    ? "complete"
    : isSurveyComplete
    ? "current"
    : "upcoming";

  // The `|| isDataMapOnboardingComplete` is because if user already completed initial data map onboarding, then all the steps should be enabled (although they still might be incomplete)
  // one thing to watch out for is that vendor list can be incomplete while data flows can be complete
  let dataFlowsStepStatus: VerticalProgressBarStepStatus = isDataFlowsFinished
    ? "complete"
    : isVendorInventoryFinished || isDataMapOnboardingComplete
    ? "current"
    : "upcoming";

  let vendorDocumentationStepStatus: VerticalProgressBarStepStatus = isVendorDocumentationFinished
    ? "complete"
    : (isVendorInventoryFinished && isDataFlowsFinished) || isDataMapOnboardingComplete
    ? "current"
    : "upcoming";

  let dataRetentionStepStatus: VerticalProgressBarStepStatus = isRetentionFinished
    ? "complete"
    : (isSurveyComplete &&
        isVendorInventoryFinished &&
        isDataFlowsFinished &&
        (isVendorDocumentationFinished ||
          onboardingProgress?.dataMapOnboardingProgress.isVendorDocumentationSkipped)) ||
      isDataMapOnboardingComplete
    ? "current"
    : "upcoming";

  // If we have only one vendor, then we know we're in the initial onboarding, so mark everything (except Vendor List) as upcoming
  if (allVendors.length < 2 && !isDataMapOnboardingComplete) {
    vendorDocumentationStepStatus = "upcoming";
    dataFlowsStepStatus = "upcoming";
    dataRetentionStepStatus = "upcoming";
  }

  return (
    <DataMapDashboard
      isSurveyComplete={isSurveyComplete}
      vendorInventory={{
        complete: isVendorInventoryFinished,
        stepStatus: vendorInventoryStepStatus,
      }}
      dataFlows={{ complete: isDataFlowsFinished, stepStatus: dataFlowsStepStatus }}
      vendorDocumentation={{
        complete: isVendorDocumentationFinished && allVendors.length > 1,
        stepStatus: vendorDocumentationStepStatus,
      }}
      dataRetention={{ complete: isRetentionFinished, stepStatus: dataRetentionStepStatus }}
    />
  );
}

function DataMapDashboardWithoutConsumerGroup() {
  const { isSurveyComplete, isVendorInventoryFinished } =
    useBusinessProfileAndVendorInventoryStatus();

  if (isSurveyComplete === null || isVendorInventoryFinished === null) {
    return <PageLoading />;
  }

  // Once Business Profile and Vendor List are complete, the Data Flows
  // tile becomes the next step the user can take. Vendor Documents and Data
  // Retention still depend on a consumer group existing, so they stay upcoming.
  const canStartDataFlows = isSurveyComplete && isVendorInventoryFinished;
  const upcomingStep: StepState = { complete: false, stepStatus: "upcoming" };

  return (
    <DataMapDashboard
      isSurveyComplete={isSurveyComplete}
      vendorInventory={{ complete: false, stepStatus: "upcoming" }}
      dataFlows={{ complete: false, stepStatus: canStartDataFlows ? "current" : "upcoming" }}
      vendorDocumentation={upcomingStep}
      dataRetention={upcomingStep}
    />
  );
}

export function DataMapDashboardPage() {
  const [org] = usePrimaryOrganization();
  const [groups] = useNonEmploymentCollectionGroups(org?.id, /* onlyUseSettled = */ true);

  if (!groups) {
    return <PageLoading />;
  }

  const consumerGroup = groups.find((cg) => cg.autocreationSlug === "ONLINE_SHOPPERS");

  if (consumerGroup) {
    return <DataMapDashboardWithConsumerGroup />;
  }

  return <DataMapDashboardWithoutConsumerGroup />;
}
